import { compose, isEqual, omit } from 'lodash/fp';
import { EntityState } from '@reduxjs/toolkit';
import { Person, PhysicalPerson } from '../../../../global/entities/Person';
import { BuyOrderRelation } from '../../../../utils/types/buy-order-relations';
import { PeopleCompareFn } from './people-types';
import { BuyOrderPersonEntity } from './people-redux';

export function personWithId(person: BuyOrderRelation<Person>) {
  return {
    firstName: person.firstName,
    lastName: person.lastName,
    socialSecurityNumber: person.socialSecurityNumber,
    city: person.city,
    address: person.address,
    postalCode: person.postalCode,
    ...(person.personId && { personId: person.personId }),
  };
}

export const personWithoutId = compose(omit('personId'), personWithId);

/** Function to detect if the `person` object is at "deep-equality" different than
 * an existing person with the same SSN.
 * */
export const isChanged = (
  state: EntityState<BuyOrderPersonEntity>,
  person?: Person,
  compareFn?: PeopleCompareFn,
) => {
  if (!person) {
    return false;
  }

  const existing = state.entities[person.socialSecurityNumber];
  // If person with that SSN doesn't exist in table, treat it as a "change"
  if (!existing) {
    return true;
  }

  return compareFn
    ? compareFn(person, existing)
    : !isEqual(personWithoutId(existing), personWithoutId(person));
};

export const getChangesBatch = (
  state: EntityState<BuyOrderPersonEntity>,
  people: (Person | PhysicalPerson | undefined)[],
  compareFn?: PeopleCompareFn,
): Person[] =>
  people
    .filter((person) => person?.socialSecurityNumber)
    .filter((person) => isChanged(state, person, compareFn)) as Person[];
